import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const STORAGE_KEY = "ms_currency";
const DEFAULT_CURRENCIES = [
    { code: "TRY", symbol: "₺", rate: 1.0 },
    { code: "USD", symbol: "$", rate: 0.029 },
    { code: "EUR", symbol: "€", rate: 0.027 },
];

const CurrencyContext = createContext({
    currency: DEFAULT_CURRENCIES[0],
    currencies: DEFAULT_CURRENCIES,
    banks: [],
    posBanks: [],
    paymentMethods: { card: false, bank_transfer: true, at_hotel: true },
    languages: [],
    setCurrencyCode: () => {},
    convert: (try_amount) => try_amount,
    format: (try_amount) => `₺${try_amount}`,
});

export function CurrencyProvider({ children }) {
    const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
    const [banks, setBanks] = useState([]);
    const [posBanks, setPosBanks] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState({ card: false, bank_transfer: true, at_hotel: true });
    const [languages, setLanguages] = useState([]);
    const [currencyCode, setCurrencyCodeState] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) || "TRY"; } catch (e) { return "TRY"; }
    });

    useEffect(() => {
        api.get("/settings/public").then((r) => {
            const d = r.data || {};
            if (d.supported_currencies?.length) setCurrencies(d.supported_currencies);
            if (Array.isArray(d.bank_accounts)) setBanks(d.bank_accounts);
            if (Array.isArray(d.pos_banks)) setPosBanks(d.pos_banks);
            if (d.payment_methods_enabled) setPaymentMethods(d.payment_methods_enabled);
            if (Array.isArray(d.supported_languages)) setLanguages(d.supported_languages);
        }).catch(() => { /* keep defaults */ });
    }, []);

    const setCurrencyCode = useCallback((code) => {
        setCurrencyCodeState(code);
        try { localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* noop */ }
    }, []);

    const currency = currencies.find((c) => c.code === currencyCode) || currencies[0];

    const convert = useCallback((tryAmount) => {
        if (tryAmount == null || isNaN(Number(tryAmount))) return 0;
        const rate = Number(currency?.rate || 1);
        return Number(tryAmount) * rate;
    }, [currency]);

    const format = useCallback((tryAmount, opts = {}) => {
        const converted = convert(tryAmount);
        const sym = currency?.symbol || "₺";
        const locale = currency?.code === "TRY" ? "tr-TR" : currency?.code === "EUR" ? "de-DE" : "en-US";
        const fractionDigits = opts.fractionDigits ?? (currency?.code === "TRY" ? 0 : 2);
        const num = converted.toLocaleString(locale, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
        // TRY → "₺1.500"; USD → "$43.50"; EUR → "€40,50"
        return currency?.code === "TRY" ? `${sym}${num}` : `${sym}${num}`;
    }, [convert, currency]);

    const value = {
        currency,
        currencies,
        banks,
        posBanks,
        paymentMethods,
        languages,
        setCurrencyCode,
        convert,
        format,
    };
    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
    return useContext(CurrencyContext);
}
